import { ceilNonNegative, isFiniteNumber, isPositiveFiniteNumber } from '@/utils/math-kit';
import {
    getFontFamilyWithFallback,
    getPlainTextFontByPretext,
    getPlainTextLayoutByPretext,
    measurePlainTextGraphemes,
    type IPlainTextGraphemeMeasurement,
} from '@/utils/text-kit';
import {
    MarkupLabelType,
    MarkupLabelUnit,
    type ILayerProps,
    type IMarkupLabel,
    type IMarkupLayerProps,
} from '@/version-types';

export interface IMarkupLabelLineLayout {
    text: string;
    width: number;
    graphemes: IPlainTextGraphemeMeasurement[];
}

export interface IMarkupLabelLayout {
    key: string;
    width: number;
    height: number;
    fontSize: number;
    fontFamily: string;
    font: string;
    lineHeight: number;
    letterSpacing: number;
    lines: IMarkupLabelLineLayout[];
}

export function getMarkupLabelFontFamily(layer: ILayerProps) {
    const { label } = layer.data as IMarkupLayerProps;
    return getFontFamilyWithFallback(label.fontFamily, layer.__runtime?.fontFamilyMap?.[label.fontFamily]);
}

export function getMarkupLabelLayoutKey(layer: ILayerProps, fontSize?: number) {
    const { label } = layer.data as IMarkupLayerProps;
    return JSON.stringify({
        text: getMarkupLabelRenderText(label),
        fontSize: fontSize ?? label.fontSize,
        lineSpacing: label.lineSpacing,
        letterSpacing: label.letterSpacing,
        fontFamily: getMarkupLabelFontFamily(layer),
        bold: label.bold,
        italic: label.italic,
        color: label.color,
    });
}

export function getMarkupLabelRenderText(label: IMarkupLabel) {
    if (label.type === MarkupLabelType.custom) {
        return label.text.trim();
    }
    if (!label.units.length) return `${label.num}`;

    const sortList = [MarkupLabelUnit.mm, MarkupLabelUnit.cm, MarkupLabelUnit.inch, MarkupLabelUnit.m];
    const conversionFactors = {
        [MarkupLabelUnit.mm]: 1,
        [MarkupLabelUnit.cm]: 10,
        [MarkupLabelUnit.m]: 1000,
        [MarkupLabelUnit.inch]: 25.4,
    };
    const unitSet = new Set<MarkupLabelUnit>(label.units);
    const units = sortList.filter((unit) => unitSet.has(unit));
    const sourceUnit = units[0] || MarkupLabelUnit.mm;
    const mmValue = label.num * conversionFactors[sourceUnit];
    const values = {
        [MarkupLabelUnit.mm]: mmValue,
        [MarkupLabelUnit.cm]: parseFloat((mmValue / conversionFactors[MarkupLabelUnit.cm]).toFixed(2)),
        [MarkupLabelUnit.m]: parseFloat((mmValue / conversionFactors[MarkupLabelUnit.m]).toFixed(2)),
        [MarkupLabelUnit.inch]: parseFloat((mmValue / conversionFactors[MarkupLabelUnit.inch]).toFixed(2)),
    };

    return units.map((unit) => `${values[unit]} ${unit}`).join('/');
}

export function createMarkupLabelLayout(layer: ILayerProps, fontSize?: number): IMarkupLabelLayout | null {
    const { label } = layer.data as IMarkupLayerProps;
    const renderText = getMarkupLabelRenderText(label);
    const resolvedFontSize = fontSize ?? label.fontSize;
    if (!renderText) return null;
    if (!isPositiveFiniteNumber(resolvedFontSize) || !isPositiveFiniteNumber(label.lineSpacing)) return null;

    const fontFamily = getMarkupLabelFontFamily(layer);
    const lineHeight = Math.ceil(resolvedFontSize * label.lineSpacing);
    const letterSpacing = isFiniteNumber(label.letterSpacing) ? label.letterSpacing : 0;

    try {
        const plainLayout = getPlainTextLayoutByPretext(renderText, {
            fontFamily,
            fontSize: resolvedFontSize,
            bold: label.bold,
            italic: label.italic,
            lineHeightPx: lineHeight,
            width: 1,
            disableAutoWrap: true,
        });
        if (!plainLayout.lineCount) return null;

        const lines: IMarkupLabelLineLayout[] = [];
        for (const line of plainLayout.lines) {
            const graphemes = letterSpacing
                ? measurePlainTextGraphemes(line.text, {
                      fontFamily,
                      fontSize: resolvedFontSize,
                      bold: label.bold,
                      italic: label.italic,
                  })
                : [];
            if (!graphemes) return null;

            const graphemeWidth = graphemes.reduce((width, grapheme) => width + grapheme.width, 0);
            const width = letterSpacing
                ? Math.max(0, graphemeWidth + Math.max(0, graphemes.length - 1) * letterSpacing)
                : line.width;
            lines.push({ text: line.text, width, graphemes });
        }

        const width = ceilNonNegative(Math.max(0, ...lines.map((line) => line.width)));
        const height = ceilNonNegative(lines.length * lineHeight);
        if (!width || !height) return null;

        return {
            key: getMarkupLabelLayoutKey(layer, resolvedFontSize),
            width,
            height,
            fontSize: resolvedFontSize,
            fontFamily,
            font: getPlainTextFontByPretext({
                fontFamily,
                fontSize: resolvedFontSize,
                bold: label.bold,
                italic: label.italic,
            }),
            lineHeight,
            letterSpacing,
            lines,
        };
    } catch {
        return null;
    }
}
